import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Download, History as HistoryIcon, FileSpreadsheet, RefreshCw, Trash2 } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function History({ api, reloadKey }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${api}/conversions`);
      setItems(res.data);
    } catch (_e) {
      toast.error("Could not load conversion history");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const downloadOne = async (id, filename) => {
    setDownloadingId(id);
    try {
      const res = await axios.get(`${api}/conversions/${id}`);
      const binStr = atob(res.data.file_b64);
      const bytes = new Uint8Array(binStr.length);
      for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.data.filename || filename || `converted_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_e) {
      toast.error("Could not fetch that file");
    } finally {
      setDownloadingId(null);
    }
  };

  const deleteOne = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${api}/conversions/${id}`);
      setItems((prev) => (prev ? prev.filter((it) => it.id !== id) : prev));
      toast.success("Removed from history");
    } catch (_e) {
      toast.error("Could not remove that entry");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card className="border-stone-200 shadow-sm rounded-xl">
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="font-heading text-2xl text-stone-900 flex items-center gap-2">
            <HistoryIcon className="w-5 h-5 text-amber-700" /> Conversion history
          </CardTitle>
          <CardDescription className="font-body text-stone-500 mt-1">
            The last 20 conversions are kept here. Click download to grab any of them again.
          </CardDescription>
        </div>
        <Button
          type="button"
          data-testid="history-refresh-btn"
          onClick={load}
          disabled={loading}
          variant="outline"
          className="rounded-full border-stone-300 text-stone-700 hover:bg-stone-100 active:scale-95 transition-colors duration-200"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {loading && items === null ? (
          <div className="animate-pulse space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-14 bg-stone-100 rounded-lg" />
            ))}
          </div>
        ) : items && items.length > 0 ? (
          <div className="rounded-xl border border-stone-200 divide-y divide-stone-100 bg-white overflow-hidden">
            {items.map((item, idx) => (
              <div
                key={item.id}
                data-testid={`history-row-${idx}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center px-4 py-4 hover:bg-stone-50 transition-colors duration-150"
              >
                <div className="md:col-span-1 hidden md:flex justify-center">
                  <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center">
                    <FileSpreadsheet className="w-4 h-4 text-amber-700" />
                  </div>
                </div>

                <div className="md:col-span-4 min-w-0">
                  <p
                    data-testid={`history-filename-${idx}`}
                    className="font-body text-sm font-medium text-stone-900 truncate"
                    title={item.filename}
                  >
                    {item.filename}
                  </p>
                  <p
                    className="font-body text-xs text-stone-500 truncate mt-0.5"
                    title={item.source_filename}
                  >
                    from {item.source_filename || "PO file"}
                  </p>
                </div>

                <div className="md:col-span-3">
                  <p
                    data-testid={`history-date-${idx}`}
                    className="font-body text-xs text-stone-600"
                  >
                    {formatDate(item.created_at)}
                  </p>
                </div>

                <div className="md:col-span-2 flex flex-wrap gap-1.5">
                  <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 text-[11px]">
                    {item.matched} matched
                  </Badge>
                  {item.unmatched > 0 && (
                    <Badge className="bg-stone-100 text-stone-600 border border-stone-200 hover:bg-stone-100 text-[11px]">
                      {item.unmatched} skipped
                    </Badge>
                  )}
                </div>

                <div className="md:col-span-2 flex justify-start md:justify-end gap-2">
                  <Button
                    type="button"
                    data-testid={`history-download-${idx}`}
                    disabled={downloadingId === item.id}
                    onClick={() => downloadOne(item.id, item.filename)}
                    className="rounded-full bg-amber-700 hover:bg-amber-800 text-white px-3 py-2 h-auto active:scale-95 transition-colors duration-200"
                  >
                    <Download className="w-4 h-4" />
                  </Button>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        data-testid={`history-delete-${idx}`}
                        disabled={deletingId === item.id}
                        variant="outline"
                        className="rounded-full border-stone-300 text-stone-500 hover:text-red-700 hover:border-red-300 hover:bg-red-50 px-3 py-2 h-auto active:scale-95 transition-colors duration-200"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid={`delete-confirm-dialog-${idx}`}>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading">
                          Remove this conversion?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="font-body">
                          <span className="block text-stone-700 font-medium">{item.filename}</span>
                          <span className="block mt-1">
                            It will be permanently deleted from history and can&apos;t be re-downloaded.
                          </span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel
                          data-testid={`delete-cancel-${idx}`}
                          className="rounded-full"
                        >
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          data-testid={`delete-confirm-${idx}`}
                          onClick={() => deleteOne(item.id)}
                          className="rounded-full bg-red-600 hover:bg-red-700 text-white"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            data-testid="history-empty"
            className="rounded-xl border border-dashed border-stone-300 bg-stone-50/50 p-10 text-center"
          >
            <div className="w-12 h-12 rounded-full bg-white border border-stone-200 mx-auto flex items-center justify-center mb-3">
              <HistoryIcon className="w-5 h-5 text-stone-400" />
            </div>
            <p className="font-heading font-semibold text-stone-900">No conversions yet</p>
            <p className="font-body text-sm text-stone-500 mt-1">
              Your converted files will show up here after your first upload.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
